import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useOnboarding, type OnboardingStep } from '@/contexts/OnboardingContext';
import { useUser } from '@/contexts/UserContext';
import { WizardShell } from './WizardShell';
import { WelcomeScreen } from './WelcomeScreen';
import { StepCompanySetup } from './steps/StepCompanySetup';
import { StepUserAccess } from './steps/StepUserAccess';
import { StepBookingTypes } from './steps/StepBookingTypes';
import { StepPlannerDefaults } from './steps/StepPlannerDefaults';
import { StepPlannerGroups } from './steps/StepPlannerGroups';
import { CompletionScreen } from './CompletionScreen';

interface OnboardingWizardProps {
  setCurrentPage: (page: string) => void;
  setSettingsTab?: (tab: string) => void;
}

export function OnboardingWizard({ setCurrentPage, setSettingsTab }: OnboardingWizardProps) {
  const {
    isWizardOpen,
    savedStep,
    saveStep,
    dismissOnboarding,
    completeOnboarding,
    markStepCompleted,
    markStepSkipped,
    stepStatuses,
  } = useOnboarding();

  const { user, bootstrapState } = useUser();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(savedStep);

  // Sync with savedStep when wizard opens
  useEffect(() => {
    if (isWizardOpen) {
      setCurrentStep(savedStep);
    }
  }, [isWizardOpen, savedStep]);

  const goToStep = useCallback(
    (step: OnboardingStep) => {
      setCurrentStep(step);
      saveStep(step);
    },
    [saveStep]
  );

  const handleNext = useCallback(() => {
    if (currentStep >= 1 && currentStep <= 5) {
      markStepCompleted(currentStep);
    }
    if (currentStep === 5) {
      goToStep(6);
    } else {
      goToStep(((currentStep + 1) as OnboardingStep));
    }
  }, [currentStep, goToStep, markStepCompleted]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      goToStep(((currentStep - 1) as OnboardingStep));
    }
  }, [currentStep, goToStep]);

  const handleSkipStep = useCallback(() => {
    if (currentStep >= 1 && currentStep <= 5) {
      markStepSkipped(currentStep);
    }
    if (currentStep === 5) {
      goToStep(6);
    } else {
      goToStep(((currentStep + 1) as OnboardingStep));
    }
  }, [currentStep, goToStep, markStepSkipped]);

  const handleComplete = useCallback(() => {
    completeOnboarding();
  }, [completeOnboarding]);

  const handleNavigateToSettings = useCallback(
    (tab: string) => {
      completeOnboarding();
      if (setSettingsTab) setSettingsTab(tab);
      setCurrentPage('settings');
    },
    [completeOnboarding, setCurrentPage, setSettingsTab]
  );

  const handleNavigateToPage = useCallback(
    (page: string) => {
      completeOnboarding();
      setCurrentPage(page);
    },
    [completeOnboarding, setCurrentPage]
  );

  const handleNavigateToResources = useCallback(() => {
    setCurrentPage('resources');
  }, [setCurrentPage]);

  if (!isWizardOpen || bootstrapState === 'loading') return null;

  const firstName = user.name.split(' ')[0] || user.name;

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <WelcomeScreen
            firstName={firstName}
            onStart={() => goToStep(1)}
            onSkip={dismissOnboarding}
          />
        );
      case 1:
        return (
          <StepCompanySetup
            onNext={handleNext}
            onSkip={handleSkipStep}
          />
        );
      case 2:
        return (
          <StepUserAccess
            onNext={handleNext}
            onBack={handleBack}
            onSkip={handleSkipStep}
            onNavigateToResources={handleNavigateToResources}
          />
        );
      case 3:
        return (
          <StepBookingTypes
            onNext={handleNext}
            onBack={handleBack}
            onSkip={handleSkipStep}
          />
        );
      case 4:
        return (
          <StepPlannerDefaults
            onNext={handleNext}
            onBack={handleBack}
            onSkip={handleSkipStep}
          />
        );
      case 5:
        return (
          <StepPlannerGroups
            onNext={handleNext}
            onBack={handleBack}
            onSkip={handleSkipStep}
          />
        );
      case 6:
        return (
          <CompletionScreen
            stepStatuses={stepStatuses}
            onComplete={handleComplete}
            onNavigateToSettings={handleNavigateToSettings}
            onNavigateToPage={handleNavigateToPage}
          />
        );
      default:
        return null;
    }
  };

  return ReactDOM.createPortal(
    <WizardShell
      currentStep={currentStep}
      stepStatuses={stepStatuses}
      onSkip={currentStep !== 6 ? dismissOnboarding : undefined}
    >
      {renderStep()}
    </WizardShell>,
    document.body
  );
}
